import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../services/apiClient';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, unknown>
          ) => void;
        };
      };
    };
  }
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const ButtonHolder = styled.div`
  display: flex;
  justify-content: center;
  /* The Google-rendered button comes in a fixed iframe; just center it. */
  & > div {
    width: 100% !important;
    max-width: 400px;
  }
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
`;

interface Props {
  onSuccess: (idToken: string) => Promise<void> | void;
  onError?: (message: string) => void;
}

export function GoogleSignInButton({ onSuccess, onError }: Props) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [clientId, setClientId] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    apiClient
      .get<{ googleClientId: string }>('/api/auth/google-config')
      .then((cfg) => setClientId(cfg.googleClientId || ''))
      .catch(() => setClientId(''));
  }, []);

  useEffect(() => {
    if (!clientId || !buttonRef.current) return;

    let cancelled = false;
    const tryInit = (attempts = 0) => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        if (attempts > 50) return; // ~5s of retries — script likely blocked
        setTimeout(() => tryInit(attempts + 1), 100);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            await onSuccess(response.credential);
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Google sign-in failed';
            setError(msg);
            onError?.(msg);
          }
        },
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(buttonRef.current!, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: 320,
      });
    };
    tryInit();

    return () => { cancelled = true; };
  }, [clientId, onSuccess, onError]);

  if (!clientId) return null;

  return (
    <Wrap>
      <Divider>or</Divider>
      <ButtonHolder ref={buttonRef} />
      {error && <ErrorText>{error}</ErrorText>}
    </Wrap>
  );
}
